/**
 * "The row was saved, and here is the one that already existed."
 *
 * The create routes answer a duplicate registry code with a 409 and write
 * nothing - that case is handled where the save is attempted. This is the other
 * one: the company is on the OTHER table, the row WAS written, and the operator
 * needs to know before they start working a lead that has been a paying client
 * since 2021.
 *
 * It is a link, never a count. A strip that says "1 similar record" is a
 * question the reader cannot answer without leaving what they were doing, so
 * this carries the name and opens the record in a new tab - the picker inside a
 * half-finished invoice must not lose the invoice to a warning.
 */
import { useT } from "../../../i18n/I18nProvider";
import type { CrmDuplicateWarning } from "../../../lib/clients";

/** More than this and the strip is a list, which nobody reads. */
const SHOWN = 3;

export function CrossWarning({ warnings }: { warnings: CrmDuplicateWarning[] }) {
  const t = useT();
  if (!warnings.length) return null;

  return (
    <div className="space-y-2">
      {warnings.slice(0, SHOWN).map((w) => {
        const client = w.kind === "existing_client";
        const base = client ? "leads.alsoClient" : "clients.alsoLead";
        // The rule is in the sentence rather than in a tone the reader has to
        // decode: a shared registry code is the same company, a shared name only
        // looks like it.
        const text = t(`${base}${w.rule === "reg_code" ? "ByRegCode" : "ByName"}`, { name: w.name });
        const href = client ? `/clients/${w.id}` : `/leads/${w.id}`;
        return (
          <div
            key={`${w.kind}:${w.id}`}
            className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400"
          >
            <span>{text}</span>
            <a
              href={href}
              target="_blank"
              rel="noreferrer"
              className="shrink-0 rounded-md border border-amber-500/40 px-2 py-1 font-medium hover:bg-amber-500/10"
            >
              {client ? t("clients.open") : t("leads.openLead")}
            </a>
          </div>
        );
      })}
    </div>
  );
}
